import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Project, ProjectTask } from '@/types'
import { queueSync, getCurrentUserId, generateUUID } from '@/lib/sync'

interface ProjectState {
  projects: Project[]

  // Actions
  addProject: (project: Omit<Project, 'id' | 'userId' | 'currentHp' | 'completed' | 'createdAt' | 'tasks'>, tasks: string[]) => void
  removeProject: (id: string) => void
  completeProjectTask: (projectId: string, taskId: string) => { damage: number; defeated: boolean } | null
  getProject: (id: string) => Project | undefined
  getActiveProjects: () => Project[]
  getCompletedProjects: () => Project[]
}

const generateId = generateUUID

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      projects: [],

      addProject: (project, taskTitles) => {
        // Calculate HP based on number of tasks (each task = 10-20 HP)
        const tasks: ProjectTask[] = taskTitles.map((title) => ({
          id: generateId(),
          projectId: '', // Will be set below
          title,
          damage: Math.floor(10 + Math.random() * 10), // 10-20 damage per task
          completed: false,
          completedAt: null,
        }))

        const totalHp = tasks.reduce((sum, t) => sum + t.damage, 0)

        const newProject: Project = {
          id: generateId(),
          userId: getCurrentUserId(),
          title: project.title,
          description: project.description,
          totalHp,
          currentHp: totalHp,
          xpReward: project.xpReward,
          completed: false,
          createdAt: new Date().toISOString(),
          tasks: tasks.map((t) => ({ ...t, projectId: '' })), // projectId not needed locally
        }

        set((state) => ({
          projects: [...state.projects, newProject],
        }))

        // Queue for Supabase sync
        queueSync('projects', 'create', newProject.id, {
          user_id: newProject.userId,
          title: newProject.title,
          description: newProject.description,
          total_hp: newProject.totalHp,
          current_hp: newProject.currentHp,
          xp_reward: newProject.xpReward,
          completed: newProject.completed,
          created_at: newProject.createdAt,
        })
      },

      removeProject: (id) => {
        set((state) => ({
          projects: state.projects.filter((p) => p.id !== id),
        }))

        // Queue for Supabase sync
        queueSync('projects', 'delete', id, {})
      },

      completeProjectTask: (projectId, taskId) => {
        const state = get()
        const project = state.projects.find((p) => p.id === projectId)
        if (!project || !project.tasks) return null

        const task = project.tasks.find((t) => t.id === taskId)
        if (!task || task.completed) return null

        const newHp = Math.max(0, project.currentHp - task.damage)
        const defeated = newHp === 0

        set((state) => ({
          projects: state.projects.map((p) => {
            if (p.id !== projectId) return p

            return {
              ...p,
              currentHp: newHp,
              completed: defeated,
              tasks: p.tasks?.map((t) =>
                t.id === taskId
                  ? { ...t, completed: true, completedAt: new Date().toISOString() }
                  : t
              ),
            }
          }),
        }))

        // Queue for Supabase sync
        queueSync('projects', 'update', projectId, {
          current_hp: newHp,
          completed: defeated,
        })

        return { damage: task.damage, defeated }
      },

      getProject: (id) => get().projects.find((p) => p.id === id),

      getActiveProjects: () => get().projects.filter((p) => !p.completed),

      getCompletedProjects: () => get().projects.filter((p) => p.completed),
    }),
    {
      name: 'waifudo-projects',
    }
  )
)
